import { useMemo } from 'react';
import { Alert, Box, Stack, TextField, Typography, Chip } from '@mui/material';
import { MyLocation } from '@mui/icons-material';
import ZoneMapPanel from '../maps/ZoneMapPanel';

interface RestaurantLocationPickerProps {
  latitude: string;
  longitude: string;
  onChange: (lat: string, lng: string) => void;
}

function parseCoord(value: string): number | null {
  if (!value.trim()) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export default function RestaurantLocationPicker({ latitude, longitude, onChange }: RestaurantLocationPickerProps) {
  const lat = parseCoord(latitude);
  const lng = parseCoord(longitude);
  const hasCoords = lat != null && lng != null;

  const center = useMemo(
    () => (hasCoords ? { lat: lat!, lng: lng! } : undefined),
    [hasCoords, lat, lng],
  );

  const setCoords = (nextLat: number, nextLng: number) => {
    onChange(nextLat.toFixed(6), nextLng.toFixed(6));
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <MyLocation fontSize="small" color="primary" />
        <Typography variant="subtitle2" fontWeight={700}>Restaurant location</Typography>
        {hasCoords && (
          <Chip size="small" label={`${lat!.toFixed(5)}, ${lng!.toFixed(5)}`} color="success" variant="outlined" />
        )}
      </Stack>
      <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
        Click the map to pin the restaurant. Coordinates power nearby search and delivery radius.
      </Alert>
      <ZoneMapPanel
        height={220}
        center={center}
        zoom={hasCoords ? 15 : 11}
        pin={hasCoords ? { lat: lat!, lng: lng! } : null}
        onMapClick={(clickLat, clickLng) => setCoords(clickLat, clickLng)}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <TextField
          size="small"
          label="Latitude"
          type="number"
          value={latitude}
          onChange={(e) => onChange(e.target.value, longitude)}
          inputProps={{ step: 0.00001 }}
          fullWidth
        />
        <TextField
          size="small"
          label="Longitude"
          type="number"
          value={longitude}
          onChange={(e) => onChange(latitude, e.target.value)}
          inputProps={{ step: 0.00001 }}
          fullWidth
        />
      </Stack>
    </Box>
  );
}
